// =============================================
// Author:      JUNAID HASSAN
// Create date: 2025-08-21
// Description: Address tab bound to NEW overlay API.
//              - Loads overlay payload: { data, flags, pendingCount, ... }
//              - Saves a DRAFT, then re-reads overlay
//              - Shows a banner when there are unsent changes
//              - Lazy-loads only once when tab first becomes active
// =============================================
// =============================================
// Author:      JUNAID HASSAN
// Update date: 2025-08-24
// Description: Address tab bound to NEW overlay API.
// =============================================

import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react';
import {
  CSpinner, CButton, CFormInput, CFormSelect, CBadge, CFormFeedback,
} from '@coreui/react';
import { useTranslation } from '../../utils/i18nStub';
import { fetchAddressOverlay, saveAddressDraft } from '../../services/employeeAddressService';
import { fetchRegions, fetchCitiesByRegion } from '../../services/geoService';
// NEW: upload helpers for temp files (thumbnail + full)
// Use the Blob/ObjectURL path only (works with JWT)
import { uploadTemp, fetchThumbBlob, blobToObjectUrl, openUploadInNewTab } from '../../services/employeeAddressService';

const empty = {
  address_line1: '', address_line2: '', district: '',
  city: '', region: '', region_id: '', city_id: '',
  postal_code: '', building_no: '', additional_no: '', unit_no: '',
  // NEW: hold upload GUID returned by /api/uploads
  proof_of_national_address_upload_id: '',
};

const POSTAL_REGEX = /^\d{5}$/;

// ---------- helpers ----------
const firstNonEmpty = (...vals) => {
  for (const v of vals) if (v !== undefined && v !== null && String(v).trim() !== '') return String(v);
  return '';
};

const normalizeDataFromServer = (data = {}) => ({
  address_line1: firstNonEmpty(data.address_line1, data.short_address, data.ShortAddress),
  address_line2: firstNonEmpty(data.address_line2, data.street, data.Street),
  district:      firstNonEmpty(data.district, data.district_name, data.DistrictName),
  city:          firstNonEmpty(data.city, data.city_name, data.CityName),
  region:        firstNonEmpty(data.region, data.region_name, data.RegionName),
  region_id:     firstNonEmpty(data.region_id, data.RegionId) || '',
  city_id:       firstNonEmpty(data.city_id,   data.CityId)   || '',
  postal_code:   firstNonEmpty(data.postal_code, data.zip, data.PostalCode),
  building_no:   firstNonEmpty(data.building_no, data.building_number, data.BuildingNumber),
  additional_no: firstNonEmpty(data.additional_no, data.extension_number, data.ExtensionNumber, data.additionalNo),
  unit_no:       firstNonEmpty(data.unit_no, data.unitNo, data.UnitNo),

    // NEW: bring back any draft/submitted upload id (GUID string)
  proof_of_national_address_upload_id:
    firstNonEmpty(
      data.proof_of_national_address_upload_id,
      data.ProofOfNationalAddressUploadId,
      data.proofUploadId
    ),

});


const normalizeFlagsFromServer = (flags = {}) => ({
  address_line1: flags.address_line1 ?? flags.short_address ?? flags.ShortAddress,
  address_line2: flags.address_line2 ?? flags.street ?? flags.Street,
  district:      flags.district ?? flags.district_name ?? flags.DistrictName,
  city_id:       flags.city_id ?? flags.CityId,
  city:          flags.city ?? flags.city_name ?? flags.CityName,
  region_id:     flags.region_id ?? flags.RegionId, 
  region:        flags.region ?? flags.region_name ?? flags.RegionName,
  postal_code:   flags.postal_code ?? flags.zip ?? flags.PostalCode,
  building_no:   flags.building_no ?? flags.building_number ?? flags.BuildingNumber,
  additional_no: flags.additional_no ?? flags.extension_number ?? flags.ExtensionNumber ?? flags.additionalNo,
  unit_no:       flags.unit_no ?? flags.unitNo ?? flags.UnitNo,
  // NEW: show "مسودة" badge when upload id is in draft
  proof_of_national_address_upload_id:
  flags.proof_of_national_address_upload_id ??
  flags.ProofOfNationalAddressUploadId ??
  flags.proofUploadId,
});

// Resolve a label from an ID using an options array [{value,label}]
const labelFromId = (id, options) => {
  if (id === null || id === undefined || String(id).trim() === '') return '';
  const hit = options.find(o => Number(o.value) === Number(id));
  return hit?.label || '';
};

const EmployeeAddressInfo = forwardRef(({ active = false }, ref) => {
  const { t } = useTranslation(['address', 'common']);

  // overlay state
  const [form, setForm] = useState(empty);
  const [flags, setFlags] = useState({});
  const [meta,  setMeta]  = useState({ pendingCount: 0, lastSavedAt: null });

  // view state
  const [loading, setLoading]     = useState(false);
  const [working, setWorking]     = useState(false);
  const [error,   setError]       = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [snapshot, setSnapshot]   = useState(empty);
  const [errors,  setErrors]      = useState({ postal_code: null });
  const [loadedOnce, setLoadedOnce] = useState(false);

  // NEW: geo lookups + selected ids (for view-mode label resolution)
  const [regionOptions, setRegionOptions] = useState([]);
  const [cityOptions,   setCityOptions]   = useState([]);
  const [regionId, setRegionId] = useState(null);
  const [cityId,   setCityId]   = useState(null);

    // NEW: file-upload view state (only affects the upload block)
  const [uploadBusy, setUploadBusy]   = useState(false);
  const [uploadError, setUploadError] = useState(null);
  
  // NEW: holds the runtime ObjectURL for the thumbnail we fetch with JWT
  const [proofThumbUrl, setProofThumbUrl] = useState(null);
  const fileRef = useRef(null);
  // open picker
  const pickProofFile = () => fileRef.current?.click();

  const onProofFilePicked = async (e) => {
      const f = e.target.files?.[0];
      e.target.value = '';            // allow re-pick same file next time
      if (!f) return;
      await handleProofUpload(f);     // you already have this
  };

  useImperativeHandle(ref, () => ({
    refreshOverlay: async () => { await loadOverlay(true); },
    getOverlaySnapshot: () => ({ data: { ...form }, flags: { ...flags }, meta: { ...meta } }),
  }));

  // whenever you know the upload id (from overlay flags/data you’re already binding):
      useEffect(() => {
      const id = form?.proof_of_national_address_upload_id;
      if (!id) { setProofThumbUrl(null); return; }

      let alive = true;
      (async () => {
        try {
          const blob = await fetchThumbBlob(id);
          const url = blobToObjectUrl(blob);
          if (alive) setProofThumbUrl(url);
        } catch {
          if (alive) setProofThumbUrl(null);
        }
      })();

      // Revoke the URL that was current when the effect ran
      const urlToRevoke = proofThumbUrl;
      return () => {
        alive = false;
        if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form?.proof_of_national_address_upload_id]);

  // Load regions once (labels come ready from SQL)
  useEffect(() => {
    (async () => {
      try {
        const rows = await fetchRegions();
        setRegionOptions(
          rows.map(r => ({
            value: r.region_id ?? r.id ?? r.RegionId,
            label: r.region_name || r.name, // no FE concatenation
          }))
        );
      } catch { /* ignore */ }
    })();
  }, []);

  // Load cities when we know the region id (from edit form or view state)
  useEffect(() => {
    const rid = (isEditing ? form.region_id : regionId) || regionId || form.region_id;
    if (!rid) { setCityOptions([]); return; }
    (async () => {
      try {
        const rows = await fetchCitiesByRegion(rid);
        setCityOptions(
          rows.map(c => ({
            value: c.city_id ?? c.id ?? c.CityId,
            label: c.city_name || c.name,
          }))
        );
      } catch { /* ignore */ }
    })();
  }, [isEditing, form.region_id, regionId]);

  const loadOverlay = async (force = false) => {
    if (!active && !force) return;
    setLoading(true); setError(null);
    try {
      const payload = await fetchAddressOverlay();
      const serverData  = payload?.data  || {};
      const serverFlags = payload?.flags || {};

      // normal UI values
      const uiData  = normalizeDataFromServer(serverData);
      const uiFlags = normalizeFlagsFromServer(serverFlags);

      setForm(uiData);
      setFlags(uiFlags);
      setMeta({
        pendingCount: payload?.pendingCountTab ?? payload?.pendingCountTab ?? 0,
        lastSavedAt:  payload?.lastSavedAt ?? null,
      });
      setSnapshot(uiData);

      // remember ids (if overlay sends them)
      setRegionId(serverData.region_id ?? serverData.RegionId ?? null);
      setCityId(serverData.city_id ?? serverData.CityId ?? null);

      setLoadedOnce(true);
    } catch (err) {
      console.error('fetchAddressOverlay error', err);
      setError(err?.message || 'Failed to load address info');
    } finally {
      setLoading(false);
    }
  };

  // Lazy-load once when the tab first becomes active
  useEffect(() => {
    if (!active || loadedOnce) return;
    loadOverlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, loadedOnce]);

  const handleEdit = () => { setSnapshot({ ...form }); setIsEditing(true); setError(null); };
  const handleCancel = () => { setForm({ ...snapshot }); setIsEditing(false); setError(null); setErrors({ postal_code: null }); };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'postal_code') {
      setErrors(e => ({ ...e, postal_code: value ? (POSTAL_REGEX.test(String(value)) ? null : 'postal_invalid') : null }));
    }
  };

  const handleSave = async () => {
    if (errors.postal_code) return;
    setWorking(true); setError(null);
    try {
      const payload = await saveAddressDraft(form);
      const serverData  = payload?.data  || {};
      const serverFlags = payload?.flags || {};
      const uiData  = normalizeDataFromServer(serverData);
      const uiFlags = normalizeFlagsFromServer(serverFlags);

      setForm(uiData);
      setFlags(uiFlags);
      setMeta({
        pendingCount: payload?.pendingCountTab ?? payload?.pendingCountTab ?? 0,
        lastSavedAt:  payload?.lastSavedAt ?? null,
      });
      setSnapshot(uiData);

      // refresh ids if server echoed them
      setRegionId(serverData.region_id ?? serverData.RegionId ?? regionId);
      setCityId(serverData.city_id ?? serverData.CityId ?? cityId);

      setIsEditing(false);
    } catch (err) {
      console.error('saveAddressDraft error', err);
      setError(err?.message || 'Update failed');
    } finally {
      setWorking(false);
    }
  };

  // NEW: Upload a single image to /api/uploads then capture the returned uploadId
  const handleProofUpload = async (file) => {
    if (!file) return;
    setUploadError(null);
    setUploadBusy(true);
    try {
      const meta = await uploadTemp(file); // { uploadId, url, ... }
      setForm(prev => ({
        ...prev,
        proof_of_national_address_upload_id: meta?.uploadId || ''
      }));
    } catch (err) {
      console.error('uploadTemp error', err);
      setUploadError(err?.message || 'Upload failed');
    } finally {
      setUploadBusy(false);
    }
  };

  // NEW: Remove the selected upload id (UI only; file remains in temp store until submitted/expired)
  const clearProofUpload = () => {
    setForm(prev => ({ ...prev, proof_of_national_address_upload_id: '' }));
    setUploadError(null);
  };

  const fmtDate = (d) => (d ? String(d).split('T')[0] : '—');

  // VIEW MODE label fallbacks from ids
  const displayRegion = form.region || labelFromId(regionId, regionOptions);
  const displayCity   = form.city   || labelFromId(cityId,   cityOptions);

  const cityIsDraft   = (flags.city === 'draft')   || (flags.city_id === 'draft');
  const regionIsDraft = (flags.region === 'draft') || (flags.region_id === 'draft');

  if (!active && !loadedOnce) return null;
  if (loading) return <CSpinner />;

  return (
    <div className="space-y-4" dir="rtl">
      {meta.pendingCount > 0 && (
        <div className="rounded-2xl border border-yellow-200 bg-yellow-50 text-yellow-900 px-4 py-3">
          {t('common:unsent_changes_with_count', 'لديك {{count}} تغييرات غير مرسلة', { count: meta.pendingCount })}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h5 className="mb-0 text-xl font-bold text-gray-900">{t('tabs.address', 'عنوان السكن')}</h5>
        {!isEditing ? (
          <button
            type="button"
            onClick={handleEdit}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {t('btn.edit', 'تعديل')}
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={working || !!errors.postal_code}
              className="inline-flex items-center gap-2 rounded-xl bg-sa-600 text-white px-4 py-2 text-sm font-semibold hover:bg-sa-700 transition-colors disabled:opacity-50"
            >
              {working ? <CSpinner size="sm" /> : t('btn.save', 'حفظ التغييرات')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={working}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              {t('btn.cancel', 'إلغاء')}
            </button>
          </div>
        )}
      </div>

      {error && <div className="text-danger mb-2">{error}</div>}

      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex flex-col text-right">
          <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">{t('address_line1', 'العنوان 1')}</span>
          <CFormInput
            value={form.address_line1 || ''}
            onChange={(e) => handleChange('address_line1', e.target.value)}
            placeholder={t('address_line1', 'العنوان التفصيلي (سطر 1)')}
            className={`rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 ${!isEditing ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
            readOnly={!isEditing}
            disabled={!isEditing}
          />
          {!isEditing && form.address_line1 && flags?.address_line1 === 'draft' && (
            <span className="text-xs text-sa-700 mt-1 inline-flex items-center gap-1">
              <CBadge color="info">{t('common:draft', 'مسودة')}</CBadge>
            </span>
          )}
        </div>
 
        <div className="flex flex-col text-right">
          <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">{t('address_line2', 'العنوان 2')}</span>
          <CFormInput
            value={form.address_line2 || ''}
            onChange={(e) => handleChange('address_line2', e.target.value)}
            placeholder={t('address_line2', 'العنوان التفصيلي (سطر 2)')}
            className={`rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 ${!isEditing ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
            readOnly={!isEditing}
            disabled={!isEditing}
          />
          {!isEditing && form.address_line2 && flags?.address_line2 === 'draft' && (
            <span className="text-xs text-sa-700 mt-1 inline-flex items-center gap-1">
              <CBadge color="info">{t('common:draft', 'مسودة')}</CBadge>
            </span>
          )}
        </div>
 
        <div className="flex flex-col text-right">
          <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">{t('region', 'المنطقة')}</span>
          {isEditing ? (
            <CFormSelect
              value={form.region_id || ''}
              onChange={async (e) => {
                const rid = e.target.value;
                setForm(prev => ({ ...prev, region_id: rid, region: '', city_id: '', city: '' }));
                setRegionId(rid);
              }}
              className="rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
            >
              <option value="">{t('region', 'اختر المنطقة')}</option>
              {regionOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </CFormSelect>
          ) : (
            <CFormInput
              value={displayRegion || ''}
              readOnly
              disabled
              placeholder={t('region', 'اختر المنطقة')}
              className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
            />
          )}
          {!isEditing && displayRegion && regionIsDraft && (
            <span className="text-xs text-sa-700 mt-1 inline-flex items-center gap-1">
              <CBadge color="info">{t('common:draft', 'مسودة')}</CBadge>
            </span>
          )}
        </div>
 
        <div className="flex flex-col text-right">
          <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">{t('city', 'المدينة')}</span>
          {isEditing ? (
            <CFormSelect
              value={form.city_id || ''}
              onChange={(e) => {
                const cid = e.target.value;
                setForm(prev => ({ ...prev, city_id: cid, city: '' }));
                setCityId(cid);
              }}
              disabled={!((form.region_id || regionId))}
              className="rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 disabled:bg-gray-100 disabled:text-gray-400"
            >
              <option value="">{t('city', 'اختر المدينة')}</option>
              {cityOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </CFormSelect>
          ) : (
            <CFormInput
              value={displayCity || ''}
              readOnly
              disabled
              placeholder={t('city', 'اختر المدينة')}
              className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
            />
          )}
          {!isEditing && displayCity && cityIsDraft && (
            <span className="text-xs text-sa-700 mt-1 inline-flex items-center gap-1">
              <CBadge color="info">{t('common:draft', 'مسودة')}</CBadge>
            </span>
          )}
        </div>

        <div className="flex flex-col text-right">
          <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">{t('district', 'الحي')}</span>
          <CFormInput
            value={form.district || ''}
            onChange={(e) => handleChange('district', e.target.value)}
            placeholder={t('district', 'اسم الحي')}
            className={`rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 ${!isEditing ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
            readOnly={!isEditing}
            disabled={!isEditing}
          />
          {!isEditing && form.district && flags?.district === 'draft' && (
            <span className="text-xs text-sa-700 mt-1 inline-flex items-center gap-1">
              <CBadge color="info">{t('common:draft', 'مسودة')}</CBadge>
            </span>
          )}
        </div>

        <div className="flex flex-col text-right">
          <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">{t('postal_code', 'الرمز البريدي')}</span>
          <CFormInput
            value={form.postal_code || ''}
            onChange={(e) => handleChange('postal_code', e.target.value)}
            inputMode="numeric"
            pattern={POSTAL_REGEX.source}
            title={t('common:errors.postal_invalid', 'الرمز البريدي يجب أن يتكون من 5 أرقام')}
            invalid={isEditing && !!errors.postal_code}
            className={`rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 ${!isEditing ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
            readOnly={!isEditing}
            disabled={!isEditing}
          />
          {isEditing && errors.postal_code && (
            <CFormFeedback invalid>{t('common:errors.postal_invalid', 'الرمز البريدي يجب أن يتكون من 5 أرقام')}</CFormFeedback>
          )}
          {!isEditing && form.postal_code && flags?.postal_code === 'draft' && (
            <span className="text-xs text-sa-700 mt-1 inline-flex items-center gap-1">
              <CBadge color="info">{t('common:draft', 'مسودة')}</CBadge>
            </span>
          )}
        </div>

        <div className="flex flex-col text-right">
          <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">{t('building_no', 'رقم المبنى')}</span>
          <CFormInput
            value={form.building_no || ''}
            onChange={(e) => handleChange('building_no', e.target.value)}
            placeholder={t('building_no', 'رقم المبنى')}
            className={`rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 ${!isEditing ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
            readOnly={!isEditing}
            disabled={!isEditing}
          />
          {!isEditing && form.building_no && flags?.building_no === 'draft' && (
            <span className="text-xs text-sa-700 mt-1 inline-flex items-center gap-1">
              <CBadge color="info">{t('common:draft', 'مسودة')}</CBadge>
            </span>
          )}
        </div>

        <div className="flex flex-col text-right">
          <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">{t('additional_no', 'الرقم الإضافي')}</span>
          <CFormInput
            value={form.additional_no || ''}
            onChange={(e) => handleChange('additional_no', e.target.value)}
            placeholder={t('additional_no', 'الرقم الإضافي')}
            className={`rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 ${!isEditing ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
            readOnly={!isEditing}
            disabled={!isEditing}
          />
          {!isEditing && form.additional_no && flags?.additional_no === 'draft' && (
            <span className="text-xs text-sa-700 mt-1 inline-flex items-center gap-1">
              <CBadge color="info">{t('common:draft', 'مسودة')}</CBadge>
            </span>
          )}
        </div>

        <div className="flex flex-col text-right">
          <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">{t('unit_no', 'رقم الوحدة')}</span>
          <CFormInput
            value={form.unit_no || ''}
            onChange={(e) => handleChange('unit_no', e.target.value)}
            placeholder={t('unit_no', 'رقم الوحدة')}
            className={`rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 ${!isEditing ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
            readOnly={!isEditing}
            disabled={!isEditing}
          />
          {!isEditing && form.unit_no && flags?.unit_no === 'draft' && (
            <span className="text-xs text-sa-700 mt-1 inline-flex items-center gap-1">
              <CBadge color="info">{t('common:draft', 'مسودة')}</CBadge>
            </span>
          )}
        </div>

        <div className="md:col-span-2 flex flex-col text-right">
          <span className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">{t('proof_of_national_address', 'إثبات العنوان الوطني')}</span>

          {!isEditing ? (
            // -------- VIEW MODE --------
            form?.proof_of_national_address_upload_id ? (
              <>
                {proofThumbUrl ? (
                  <img
                    src={proofThumbUrl}
                    alt="thumb"
                    style={{ maxWidth: 120, maxHeight: 120, objectFit: 'cover', borderRadius: 6, marginInlineEnd: 8 }}
                  />
                ) : (
                  <span className="text-muted me-2">{t('common:loading', 'جاري التحميل…')}</span>
                )}
                <CButton
                  size="sm"
                  color="link"
                  className="p-0 align-baseline"
                  onClick={() => openUploadInNewTab(form.proof_of_national_address_upload_id, 'proof')}
                >
                  {t('common:view_or_download', 'عرض/تنزيل')}
                </CButton>
              </>
            ) : (
              <span className="text-muted">—</span>
            )
          ) : (
            // -------- EDIT MODE --------
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {form?.proof_of_national_address_upload_id && (
                <>
                  {proofThumbUrl ? (
                    <img
                      src={proofThumbUrl}
                      alt="thumb"
                      style={{ maxWidth: 120, maxHeight: 120, objectFit: 'cover', borderRadius: 6 }}
                    />
                  ) : (
                    <span className="text-muted">{t('common:loading', 'جاري التحميل…')}</span>
                  )}
                </>
              )}

              {/* hidden input to pick a file */}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={onProofFilePicked}
                style={{ display: 'none' }}
              />

              <CButton
                size="sm"
                color="primary"
                disabled={uploadBusy}
                onClick={pickProofFile}
              >
                {uploadBusy ? <CSpinner size="sm" /> : (form?.proof_of_national_address_upload_id ? t('common:replace', 'استبدال') : t('common:choose_file', 'اختيار ملف'))}
              </CButton>

              {form?.proof_of_national_address_upload_id && (
                <CButton
                  size="sm"
                  color="danger"
                  variant="outline"
                  disabled={uploadBusy}
                  onClick={clearProofUpload}
                >
                  {t('common:remove', 'إزالة')}
                </CButton>
              )}

              {uploadError && <span className="text-danger ms-2">{uploadError}</span>}
            </div>
          )}
        </div>

        <div className="flex flex-col text-right">
          <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">{t('updated_on', 'آخر تعديل')}</span>
          <CFormInput
            value={fmtDate(meta.lastSavedAt) || ''}
            readOnly
            disabled
            className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
          />
        </div>
        <div className="flex flex-col text-right">
          <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">{t('profile:created_on', 'تاريخ الإنشاء')}</span>
          <CFormInput
            value={fmtDate(form.created_dt) || ''}
            readOnly
            disabled
            className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
          />
        </div>
        </div>
      </div>
    </div>
  );
});

export default EmployeeAddressInfo;
